import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { generalConfigFromGlobal } from '../../../shared/workspace-element-context';
import { ScreenVideo } from '../../../screen-video-container/screen-video-list/screen-video';
import { ScreenVideoUploadService } from '../../../screen-video-container/screen-video-upload/screen-video-upload.service';

@Component({
    selector: 'gtw-video-upload',
    templateUrl: './video-upload.component.html',
    styleUrls: ['./video-upload.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class VideoUploadComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  inProgress = false;
  durationInSeconds = 5;

  private video!: HTMLVideoElement;
  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D | null;

  @ViewChild('posterFile')
  posterFile!: ElementRef<HTMLImageElement>;

  @ViewChild('videoFileInput')
  videoFileInput!: ElementRef<HTMLInputElement>;

  @Output()
  cancelDocModal = new EventEmitter<string>();
  @Output('close-model') closeModal = new EventEmitter<any>();

  @Input('updateScreenVideo')
  set updateScreenVideoInput(video: ScreenVideo) {
    if (video && Object.keys(video).length > 0) {
      this.uploadScreenVideoForm = new UntypedFormGroup({
        videoId: new UntypedFormControl(video.video_id, [Validators.required]),
        videoName: new UntypedFormControl(video.video_name, [Validators.required]),
        videoDescription: new UntypedFormControl(video.video_desc, [Validators.required]),
        clientKey: new UntypedFormControl(this.clientKeyNum, [Validators.required]),
        screenKey: new UntypedFormControl(this.screenKeyNum, [Validators.required]),
        videoFile: new UntypedFormControl(''),
        posterFile: new UntypedFormControl(''),
        is_client_specific: new UntypedFormControl(video.is_client_specific == 'false' ? '' : 'true'),
      });
    }
    this.screenVideoFileName = '';
    this.canvas = this.renderer.createElement('video');
  }

  @Output()
  refreshScreenVideoList = new EventEmitter<boolean>();

  screenVideoFileName: string = '';
  selectedTime = 1;
  step = 1;
  selectedTimeChanged: Subject<number> = new Subject<number>();
  selectedVideoDuration = 0;
  progress = 75;

  uploadScreenVideoForm = new UntypedFormGroup({
    videoId: new UntypedFormControl(''),
    videoName: new UntypedFormControl('', [Validators.required]),
    videoDescription: new UntypedFormControl('', [Validators.required]),
    clientKey: new UntypedFormControl(0, [Validators.required]),
    screenKey: new UntypedFormControl(0, [Validators.required]),
    videoFile: new UntypedFormControl('', [Validators.required]),
    posterFile: new UntypedFormControl('', [Validators.required]),
    is_client_specific: new UntypedFormControl(false),
  });

  constructor(
    public gtwGlobal: GlobalService,
    public gtwWorkspace: WorkspaceService,
    public gtwApp: AppService,
    private changeDetectRef: ChangeDetectorRef,
    private screenVideoUploadService: ScreenVideoUploadService,
    private renderer: Renderer2,
    private _snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.uploadScreenVideoForm.patchValue({
      clientKey: this.clientKeyNum,
      screenKey: this.screenKeyNum,
    });
  }

  private activeScreen(): any {
    return this.gtwWorkspace.workspaceFactory?.activeScreen || {};
  }

  private getBaseUrl(): string {
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    return String(gc.base_url ?? gc.api ?? '');
  }

  get screenKeyNum(): number {
    const a = this.activeScreen();
    const raw = a.link ?? a.screenKey ?? a.screen_key;
    return parseInt(String(raw), 10);
  }

  get clientKeyNum(): number {
    const raw =
      this.gtwGlobal.global?.globalParams?.client_key ?? this.gtwApp.USER_SETTINGS?.user?.client_key;
    return parseInt(String(raw), 10);
  }

  get f() {
    return this.uploadScreenVideoForm.controls;
  }

  onFileSelected() {
    const inputFile: any = this.videoFileInput.nativeElement.files?.[0] || '';
    this.screenVideoFileName = inputFile.name;
    this.selectedTime = 1;
    this.extractFrames(inputFile);
    this.uploadScreenVideoForm.patchValue({
      videoFile: inputFile,
      screenKey: this.screenKeyNum,
      clientKey: this.clientKeyNum,
    });
    this.changeDetectRef.detectChanges();
  }

  selectFrame(value: number) {
    this.selectedTimeChanged.next(value);
  }

  private extractFrames(file: any) {
    this.video = this.renderer.createElement('video');
    this.canvas = this.renderer.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');

    this.video.muted = true;
    this.video.src = URL.createObjectURL(file);

    this.video.addEventListener('loadedmetadata', this.initCanvas, false);
    this.video.addEventListener('timeupdate', this.drawFrame, false);
    this.video.currentTime = 1;
    this.video.play();

    this.selectedTimeChanged
      .pipe(
        debounceTime(1000),
        distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((value: any) => {
        this.video.currentTime = value;
        this.selectedTime = value;
        this.video.play();
      });
  }

  private initCanvas = () => {
    this.canvas.width = this.video.videoWidth;
    this.canvas.height = this.video.videoHeight;
    this.selectedVideoDuration = Math.trunc(this.video.duration);
  };

  private drawFrame = () => {
    this.video.pause();
    this.ctx?.drawImage(this.video, 0, 0);

    this.canvas.toBlob(this.saveFrame, 'image/jpeg');
  };

  private saveFrame = (blob: any) => {
    this.posterFile.nativeElement.onload = this.revokeURL;
    this.posterFile.nativeElement.src = URL.createObjectURL(blob);
    this.uploadScreenVideoForm.patchValue({
      posterFile: blob,
    });
    this.changeDetectRef.detectChanges();
  };

  private revokeURL = () => {};

  uploadScreenVideo() {
    if (!this.uploadScreenVideoForm.valid) {
      return;
    }
    this.inProgress = true;
    const uploadFormData = new FormData();

    uploadFormData.append('videoId', this.uploadScreenVideoForm.get('videoId')?.value);
    uploadFormData.append('videoName', this.uploadScreenVideoForm.get('videoName')?.value);
    if (this.uploadScreenVideoForm.get('videoFile')?.value) {
      uploadFormData.append('file', this.uploadScreenVideoForm.get('videoFile')?.value);
    }
    uploadFormData.append('screenKey', this.uploadScreenVideoForm.get('screenKey')?.value);
    uploadFormData.append('clientKey', this.uploadScreenVideoForm.get('clientKey')?.value);
    uploadFormData.append('videoDesc', this.uploadScreenVideoForm.get('videoDescription')?.value);
    if (this.canvas.toDataURL && this.canvas) {
      uploadFormData.append('posterFile', this.canvas?.toDataURL('image/jpeg'));
    }
    const isCLientSpecific = this.uploadScreenVideoForm.get('is_client_specific')?.value;

    if (isCLientSpecific == true || isCLientSpecific == 'true') {
      uploadFormData.append('is_client_specific', 'true');
    } else {
      uploadFormData.append('is_client_specific', 'false');
    }

    this.screenVideoUploadService
      .upload(this.getBaseUrl(), uploadFormData, this.clientKeyNum).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res) => {
          this.inProgress = false;

          if (res.callSuccess == 1 || parseInt(res.callSuccess) == 1) {
            this.openSnackBar('Video Uploaded Successfully !!');
            this.refreshScreenVideoList.emit(true);
          } else {
            this.openSnackBar('Video Upload Failed ' + res.errorMessage);
          }
          this.cancel();
        },
        (error) => {
          this.openSnackBar('Video Upload Failed');
          console.log('this is the error', error);
          this.inProgress = false;
        }
      );
  }

  resetUploadScreenVideoForm() {
    this.screenVideoFileName = '';
    this.uploadScreenVideoForm.get('videoId')?.clearValidators();
    this.uploadScreenVideoForm.get('videoFile')?.setValidators([Validators.required]);
    this.uploadScreenVideoForm.get('posterFile')?.setValidators([Validators.required]);
    this.uploadScreenVideoForm.patchValue({ is_client_specific: false, videoId: '' });
  }

  private openSnackBar(message: string) {
    this._snackBar.open(message, '', {
      duration: this.durationInSeconds * 1000,
    });
  }

  cancel() {
    this.closeModal.emit('true');
    this.cancelDocModal.emit('document-modal-cancel');
  }
}
